import type { DeliverySpec, SuggestSpecKey } from '@kff/domain';
import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { when } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { Dialog, LoadError } from '../ui.js';

/**
 * Klara suggests the empty "Leverans" fields from the client's meetings (owner 2026-11-01). Two steps in one dialog:
 * pick the meetings (the newest few ticked, as for the brief), then see each suggestion with the words it rests on, all
 * ticked – "Använd valda" saves the ticked ones, "Nej tack" closes. Never overwrites: Klara is only asked for fields
 * that are still empty.
 */

const TX = texts(
  {
    title: 'Suggestions from meetings',
    loading: 'Fetching the meetings…',
    noSource: 'No notetaker is connected, so Klara has no meetings to read.',
    noMeetings: (s: string) => `No meetings with this client in ${s} in the last four months.`,
    pick: (s: string) => `Klara reads the meetings you tick in ${s} and suggests the fields that are still empty. Nothing is saved until you choose.`,
    read: 'Read the meetings',
    reading: 'Klara is reading…',
    tooMany: (n: number) => `Tick at most ${n} meetings.`,
    nothing: 'Klara found nothing about the delivery in these meetings.',
    found: (n: number) => `Klara found ${n === 1 ? 'one thing' : `${n} things`}. Untick what does not fit.`,
    use: (n: number) => `Use ${n === 1 ? 'it' : `the ${n} ticked`}`,
    noThanks: 'No thanks',
    cancel: 'Cancel',
    back: 'Other meetings'
  },
  {
    title: 'Förslag från möten',
    loading: 'Hämtar mötena…',
    noSource: 'Ingen notetaker är ansluten, så Klara har inga möten att läsa.',
    noMeetings: (s: string) => `Inga möten med den här kunden i ${s} de senaste fyra månaderna.`,
    pick: (s: string) => `Klara läser mötena du bockar i ${s} och föreslår de fält som är tomma. Inget sparas förrän du väljer.`,
    read: 'Läs mötena',
    reading: 'Klara läser…',
    tooMany: (n: number) => `Bocka högst ${n} möten.`,
    nothing: 'Klara hittade inget om leveransen i de här mötena.',
    found: (n: number) => `Klara hittade ${n === 1 ? 'en sak' : `${n} saker`}. Bocka ur det som inte stämmer.`,
    use: (n: number) => (n === 1 ? 'Använd den' : `Använd de ${n} valda`),
    noThanks: 'Nej tack',
    cancel: 'Avbryt',
    back: 'Andra möten'
  }
);

interface MeetingsView {
  source: string | null;
  meetings: { id: string; title: string; startAt: string; gist: string | null }[];
  suggested: string[];
}
export interface Suggestion {
  key: SuggestSpecKey;
  value: DeliverySpec[SuggestSpecKey];
  quote: string;
}
const MAX = 5;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function SpecSuggestDialog(p: {
  api: Api;
  projectId: string;
  label: (k: SuggestSpecKey) => string;
  show: (s: Suggestion) => string;
  onApply: (patch: Partial<DeliverySpec>) => Promise<void>;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<MeetingsView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<Suggestion[] | null>(null);
  const [chosen, setChosen] = useState<SuggestSpecKey[]>([]);
  const [fail, setFail] = useState<string | null>(null);
  useEffect(() => {
    p.api.get<MeetingsView>(`/api/projects/${p.projectId}/brief/meetings`).then((r) => (setV(r), setPicked(r.suggested)), setErr);
  }, [p.projectId]);
  const flip = <T,>(xs: T[], x: T) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);

  const read = async () => {
    setBusy(true);
    setFail(null);
    try {
      const r = await p.api.post<{ suggestions: Suggestion[] }>(`/api/projects/${p.projectId}/spec/suggest`, { meetingIds: picked });
      setFound(r.suggestions);
      setChosen(r.suggestions.map((s) => s.key));
    } catch (e) {
      setFail(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const apply = async () => {
    setBusy(true);
    setFail(null);
    try {
      await p.onApply(Object.fromEntries(found!.filter((s) => chosen.includes(s.key)).map((s) => [s.key, s.value])) as Partial<DeliverySpec>);
      p.onClose();
    } catch (e) {
      setFail(errText(e));
      setBusy(false);
    }
  };

  const foot = found ? (
    <>
      <button type="button" class="btn" onClick={() => setFound(null)}>
        {t.back}
      </button>
      <button type="button" class="btn" onClick={p.onClose}>
        {t.noThanks}
      </button>
      {found.length ? (
        <button type="button" class="btn btn-primary" disabled={busy || !chosen.length} onClick={() => void apply()}>
          {t.use(chosen.length)}
        </button>
      ) : null}
    </>
  ) : (
    <>
      <button type="button" class="btn" onClick={p.onClose}>
        {t.cancel}
      </button>
      <button type="button" class="btn btn-primary" disabled={!v?.meetings.length || !picked.length || picked.length > MAX || busy} onClick={() => void read()}>
        {busy ? t.reading : t.read}
      </button>
    </>
  );

  return (
    <Dialog title={t.title} onClose={p.onClose} foot={foot}>
      {err ? (
        <LoadError error={err} />
      ) : !v ? (
        <p class="muted">{t.loading}</p>
      ) : !v.source ? (
        <p>{t.noSource}</p>
      ) : !v.meetings.length ? (
        <p>{t.noMeetings(v.source)}</p>
      ) : found ? (
        <div class="stack">
          <p class="hint">{found.length ? t.found(found.length) : t.nothing}</p>
          {found.length ? (
            <fieldset class="fieldset dlv-sugg">
              {found.map((s) => (
                <label class="check">
                  <input type="checkbox" checked={chosen.includes(s.key)} onChange={() => setChosen(flip(chosen, s.key))} />
                  <span>
                    <strong>{p.label(s.key)}:</strong> {p.show(s)}
                    <q class="small muted dlv-quote">{s.quote}</q>
                  </span>
                </label>
              ))}
            </fieldset>
          ) : null}
        </div>
      ) : (
        <div class="stack">
          <p class="hint">{t.pick(v.source)}</p>
          <fieldset class="fieldset pb-meetings">
            {v.meetings.map((m) => (
              <label class="check">
                <input type="checkbox" checked={picked.includes(m.id)} onChange={() => setPicked(flip(picked, m.id))} />
                <span>
                  <strong>{m.title}</strong> <span class="muted small">{when(m.startAt, lang)}</span>
                  {m.gist ? <span class="small muted pb-gist">{m.gist}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
          {picked.length > MAX ? <p class="callout callout--warn">{t.tooMany(MAX)}</p> : null}
        </div>
      )}
      {fail ? (
        <p class="callout callout--warn" role="alert">
          {fail}
        </p>
      ) : null}
    </Dialog>
  );
}
